import '@awesome.me/webawesome/dist/components/button/button.js';
import '../components/breadcrumb.js';
import '../components/icon.js';
import '../components/master_data_card.js';

import { faPen } from '@fortawesome/free-solid-svg-icons';
import { Task, TaskStatus } from '@lit/task';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { loadErrorCallout } from '../components/load_error_callout.js';
import { type IAccount } from '../libraries/account_models.js';
import { editLabel, saveLabel } from '../libraries/action_copy.js';
import {
    getAccount,
    updateAccount,
} from '../libraries/api_client/account.js';
import { valueOrThrow } from '../libraries/api_result.js';
import { getUserInfo } from '../libraries/auth.js';
import { loadFailed } from '../libraries/error_copy.js';
import { FormController } from '../libraries/form_controller.js';
import { readAddress } from '../libraries/form_data.js';
import { type INoteFormField } from '../libraries/form_field_types.js';
import { formHostStyles } from '../libraries/form_host_styles.js';
import { formOutcomeOf, type FormSubmitResult, formSubmitted } from '../libraries/form_outcome.js';
import {
    emailField,
    ownAddressOverrides,
    personalDataFields,
    readEmail,
    readPersonalData,
} from '../libraries/form_presets.js';
import { onSessionUpdate } from '../libraries/local_storage.js';
import { addressRows, type IMasterDataRow, unpairedLines } from '../libraries/master_data_rows.js';
import { isCurrentPath, redirectTo, redirectToReplacing } from '../libraries/navigation.js';
import { NoticeController } from '../libraries/notice_controller.js';
import { pageHeadingStyles } from '../libraries/page_heading.js';
import { SavedRecord } from '../libraries/saved_record.js';
import { liveRegion, masterDataSkeleton, skeletonStyles } from '../libraries/skeletons.js';
import { userBreadcrumb } from '../libraries/user.js';
import { WindowEventsController } from '../libraries/window_events.js';

const masterDataRowCount = 8;

export type AccountMode = 'view' | 'edit-personal-data' | 'edit-email';

/**
 * The route each of the page's three views sits on.
 *
 * One table rather than five literals, because a save has to pin the route it came
 * from before its request and the two entries have to name the same ones the router
 * does. The paths are not the `readPath`/`readPath/edit` pair the two master-data
 * leaves use, which is why `isEditPathCurrent` cannot answer for them.
 */
const accountPaths: Readonly<Record<AccountMode, string>> = {
    'view': '/account',
    'edit-personal-data': '/account/personal-data/edit',
    'edit-email': '/account/email/edit',
};

/**
 * A one-time notice for the legacy combined "Straße Hausnummer" attribute
 * landing in `streetName` with no `houseNumber` filled in. Users created before
 * the address-field split see their old combined value there, and the notice
 * tells them to split it before saving; it goes once the first split save has
 * left a house number behind.
 * @param account - The account data.
 * @returns The note, or `false` when it does not apply.
 */
function legacyAddressNote (account: IAccount): INoteFormField | false {
    if (account.houseNumber !== '' || account.streetName === '') {
        return false;
    }

    return {
        kind: 'note',
        key: 'legacy-address',
        variant: 'warning',
        content: 'Bitte trennen Sie Straßennamen und Hausnummer in die beiden Felder. ' +
            'Die Hausnummer wird ab jetzt separat gespeichert.',
    };
}

/**
 * "Mein Konto" page showing the user's personal data and e-mail
 * address. Invitations live on the dedicated `/invitations` page
 * (see `<fm-invitations>`); the user-dropdown's "Einladungen" entry
 * is the navigation entry for them.
 */
@customElement('fm-account')
export class FmAccount extends LitElement {

    static styles = [
        formHostStyles,
        pageHeadingStyles,
        skeletonStyles,
        css`
        :host {
            display: block;
        }

        fm-master-data-card {
            margin-bottom: var(--wa-space-l);
        }

        form.fm-form-card {
            margin-bottom: var(--wa-space-l);
        }
    `,
    ];

    @property({
        type: String,
    })
    mode: AccountMode = 'view';

    /**
     * The page's own callout, which is where both of its forms report: each one
     * navigates back to the read view on success, so a message rendered inside the
     * form would leave with it.
     */
    private readonly notice = new NoticeController(this);

    /**
     * Counter bumped on every cross-tab `fm_tokens` storage event so
     * the Task's `args` shallow-equality comparison re-fires the fetch
     * for the new session. The Task's own `AbortSignal` discards any
     * in-flight responses against the previous user.
     */
    @state()
    private tokensVersion = 0;

    /**
     * What a save just stored, shown until the next load lands, which is what keeps the
     * card from rendering the previous payload after the user's own change went through.
     *
     * No identity guard beside it, unlike the two master-data leaves: `/account` has one
     * subject, and the only thing that changes it is another tab signing a different
     * account in. That is handled by bumping {@link tokensVersion} so the Task's args
     * re-fire, and by dropping this in the same handler.
     */
    private readonly saved = new SavedRecord<IAccount>(this);

    private readonly accountTask = new Task(this, {
        task: async (_args, { signal }): Promise<IAccount> => valueOrThrow(await getAccount({
            signal,
        })),
        args: (): readonly [number] => [
            this.tokensVersion,
        ],
        autoRun: true,
        onComplete: (): void => {

            // A fresh server payload wins over any prior snapshot; drop it so the next
            // render picks up `value`.
            this.saved.clear();
        },
    });

    private readonly handleRetry = (): void => {
        void this.accountTask.run();
    };

    private readonly windowEvents = new WindowEventsController(this, [

        // Auth context just changed in another tab. Bumping the counter
        // re-fires the Task via args change; its built-in AbortSignal
        // discards any in-flight responses to the previous user.
        onSessionUpdate((): void => {
            this.saved.clear();
            this.tokensVersion += 1;
        }),
    ]);

    /**
     * The currently-displayed account, preferring what a save just stored
     * over the Task's last resolved value.
     * @returns The account to render, or null while loading.
     */
    private get account (): IAccount | null {
        return this.saved.value ?? this.accountTask.value ?? null;
    }

    private readonly handleEditPersonalData = (): void => {
        this.notice.dismiss();
        redirectTo(accountPaths['edit-personal-data']);
    };

    private readonly handleEditEmail = (): void => {
        this.notice.dismiss();
        redirectTo(accountPaths['edit-email']);
    };

    /**
     * Leaves whichever form the user is in. A **replace**, for the reason
     * `master_data_edit.ts` gives at the single source: a form is a sub-state of the
     * read view, so a history entry pointing back into it sends the back button
     * somewhere the user has just left. Entering one is a plain push, which is what
     * lets the back button leave it again.
     */
    private readonly handleCancel = (): void => {
        this.notice.dismiss();
        redirectToReplacing(accountPaths.view);
    };

    private buildAccountBody (overrides: Record<string, unknown>): Record<string, unknown> {
        const { account } = this;
        const fields: Record<string, unknown> = {};

        if (account !== null) {

            // Copy every non-null field except id/email. id is never part
            // of the PUT body; email is only sent when explicitly overridden
            // by the email edit form. `title` may be null for users who
            // never set one, and the server schema rejects null.
            for (const [
                key,
                value,
            ] of Object.entries(account)) {
                if (key === 'id' || key === 'email' || value === null) {
                    continue;
                }

                fields[key] = value;
            }
        }

        return {
            ...fields,
            ...overrides,
        };
    }

    /**
     * Sends one account update, confirming it in the page's own callout because a
     * saved form navigates away from itself.
     *
     * A refusal is **returned** rather than written to that callout: reporting it there
     * while telling the form the submission succeeded runs the success tail over a
     * failure, and the form's own callout is where the user is looking anyway.
     * @param body - The JSON body.
     * @param successMessage - The German confirmation to show.
     * @returns What the request reported.
     */
    private async submitAccountUpdate (
        body: Record<string, unknown>,
        successMessage: string
    ): Promise<FormSubmitResult> {
        return formOutcomeOf(await updateAccount(body), (stored): FormSubmitResult => {

            // Absent when the server answered with a body this client cannot read.
            // The change is stored either way, so the next load is what corrects the
            // view rather than a failure the save did not have.
            if (stored !== null) {
                this.saved.apply(stored);
            }

            this.notice.notify('success', successMessage);

            return formSubmitted;
        });
    }

    /**
     * Requests the e-mail change, which the server only applies once the new
     * address has been confirmed through the link it sends.
     * @param email - The requested address.
     * @returns What the request reported.
     */
    private async saveEmail (email: string): Promise<FormSubmitResult> {
        const message = 'Eine Bestätigungs-E-Mail wurde an die neue Adresse gesendet. ' +
            'Ihre E-Mail-Adresse wird erst nach Bestätigung über den Link geändert.';

        return this.saveAccount(this.buildAccountBody({
            email,
        }), message);
    }

    /**
     * Reads the master-data form into the request body, through the two readers
     * that are the counterparts of the two field blocks it declares.
     * @param formData - The submitted values.
     * @returns The fields to store.
     */
    private static buildMasterDataBody (formData: FormData): Record<string, unknown> {
        return {
            ...readPersonalData(formData),
            ...readAddress(formData),
        };
    }

    /**
     * Saves the edited master data.
     * @param formData - The submitted values.
     * @returns What the request reported.
     */
    private async saveMasterData (formData: FormData): Promise<FormSubmitResult> {
        return this.saveAccount(
            FmAccount.buildMasterDataBody(formData),
            'Ihre Daten wurden erfolgreich gespeichert.'
        );
    }

    /**
     * Sends one account update and returns to the account page on success, which
     * is what leaves the confirmation on screen while the form goes.
     *
     * The return is a **replace** and it happens only while the user is still on the
     * form the save came from, both for the reasons `saveMasterData()`
     * (`libraries/master_data_edit.ts`) gives: the form is a sub-state of the read view,
     * and an answer landing after the user has moved on would otherwise drag them back
     * into a page they left and destroy the history entry they moved to. The route is
     * pinned **before** the request, since `mode` may already describe something else by
     * the time it answers.
     *
     * A request that rejects is deliberately not caught here: `FormController`
     * catches whatever `submit` throws and shows the same generic failure, so a
     * `try`/`catch` around this would only be a second way to say it.
     * @param body - The fields to store.
     * @param message - The German confirmation to show.
     * @returns What the request reported.
     */
    private async saveAccount (body: Record<string, unknown>, message: string): Promise<FormSubmitResult> {
        const formPath = accountPaths[this.mode];
        const result = await this.submitAccountUpdate(body, message);

        if (result.success && isCurrentPath(formPath)) {
            redirectToReplacing(accountPaths.view);
        }

        return result;
    }

    /**
     * The e-mail form. Its confirmation is the page's rather than the form's,
     * because a saved form navigates back to the read view and would take a callout
     * rendered inside it along; its refusal stays the form's.
     * @param account - The loaded account.
     * @returns What the form declares.
     */
    private readonly emailForm = new FormController(this, () => {
        const { account } = this;

        if (account === null) {
            return null;
        }

        return {
            fields: [
                emailField({
                    value: account.email,

                    // Your own address, so an example of one would be noise.
                    placeholder: false,
                }),
                {
                    kind: 'note',
                    key: 'verification-note',
                    content: 'Die Änderung der E-Mail-Adresse erfordert eine erneute Verifizierung.',
                },
            ],
            submitLabel: saveLabel,
            onCancel: (): void => this.handleCancel(),
            submit: async (formData: FormData): Promise<FormSubmitResult> => this.saveEmail(
                readEmail(formData)
            ),
        };
    });

    private renderEmailEdit (): TemplateResult | typeof nothing {
        if (this.account === null) {
            return liveRegion(masterDataSkeleton(unpairedLines(masterDataRowCount)));
        }

        return this.emailForm.render();
    }

    private renderEmailDisplay (account: IAccount): TemplateResult {
        const emailRows: Array<IMasterDataRow> = [
            {
                label: 'E-Mail-Adresse',
                value: account.email,
                copyable: true,
            },
        ];

        return html`
            <fm-master-data-card heading="E-Mail-Adresse" .rows=${emailRows}>
                <wa-button slot="actions" size="s" variant="brand"
                           @click=${this.handleEditEmail}>
                    <fm-icon slot="start" .icon=${faPen}></fm-icon>
                    ${editLabel}
                </wa-button>
            </fm-master-data-card>
        `;
    }

    private renderPersonalDataDisplay (): TemplateResult {
        const { account } = this;

        if (account === null) {
            return liveRegion(masterDataSkeleton(unpairedLines(masterDataRowCount)));
        }

        const personalRows: Array<IMasterDataRow> = [
            {
                label: 'Anrede',
                value: account.salutation,
            },
            {
                label: 'Titel',
                value: account.title,
            },
            {
                label: 'Vorname',
                value: account.firstName,
            },
            {
                label: 'Nachname',
                value: account.lastName,
            },
            ...addressRows(account),
        ];

        return html`
            <fm-master-data-card heading="Persönliche Daten" .rows=${personalRows}>
                <wa-button slot="actions" size="s" variant="brand"
                           @click=${this.handleEditPersonalData}>
                    <fm-icon slot="start" .icon=${faPen}></fm-icon>
                    ${editLabel}
                </wa-button>
            </fm-master-data-card>

            ${this.renderEmailDisplay(account)}
        `;
    }

    /**
     * The personal-data form. Its confirmation is the page's, for the reason
     * {@link emailForm} gives.
     * @param account - The loaded account.
     * @returns What the form declares.
     */
    private readonly masterDataForm = new FormController(this, () => {
        const { account } = this;

        if (account === null) {
            return null;
        }

        return {
            fields: [
                ...personalDataFields({
                    values: account,
                }),
                legacyAddressNote(account),
                {
                    kind: 'address',
                    values: account,
                    overrides: ownAddressOverrides,
                },
            ],
            submitLabel: saveLabel,
            onCancel: (): void => this.handleCancel(),
            submit: async (formData: FormData): Promise<FormSubmitResult> => this.saveMasterData(formData),
        };
    });

    private renderPersonalDataEdit (): TemplateResult | typeof nothing {
        if (this.account === null) {
            return liveRegion(masterDataSkeleton(unpairedLines(masterDataRowCount)));
        }

        return this.masterDataForm.render();
    }

    private readonly breadcrumb = userBreadcrumb(getUserInfo());

    private renderBody (): TemplateResult | typeof nothing {
        if (this.accountTask.status === TaskStatus.ERROR) {
            return nothing;
        }

        switch (this.mode) {
            case 'edit-personal-data':
                return this.renderPersonalDataEdit();
            case 'edit-email':
                return this.renderEmailEdit();
            case 'view':
            default:
                return this.renderPersonalDataDisplay();
        }
    }

    private renderHeading (): string {
        switch (this.mode) {
            case 'edit-personal-data':
                return 'Persönliche Daten bearbeiten';
            case 'edit-email':
                return 'E-Mail-Adresse bearbeiten';
            case 'view':
            default:
                return 'Mein Konto';
        }
    }

    render (): TemplateResult {
        return html`
            <fm-breadcrumb .items=${this.breadcrumb}></fm-breadcrumb>
            <h1>${this.renderHeading()}</h1>

            ${this.notice.render()}

            ${loadErrorCallout(
                this.accountTask.status === TaskStatus.ERROR,
                loadFailed('Das Konto'),
                this.handleRetry
            )}

            ${this.renderBody()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-account': FmAccount;
    }
}
